import { Link, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { cn } from '@/lib/utils';
import {
  PieChart,
  MessageSquare,
  Info,
  Layers,
  Cloud,
  FileText,
  GitCompare,
  Search,
  Users,
  Lightbulb,
  CreditCard,
  TrendingUp,
  Clock,
  Bot,
  type LucideIcon,
} from 'lucide-react';
import { useAuth, getUserInitials, getUserDisplayName } from '@/lib/auth-context';
import { isAdminUser } from '@/lib/qa-permissions';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Animated flame ring for admin users using GIF
function FlameRing({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex items-center justify-center" style={{ width: 40, height: 40 }}>
      {/* Animated flame GIF ring - using original for correct aspect ratio */}
      <img 
        src="/2306037_08c19.gif" 
        alt="" 
        className="absolute pointer-events-none object-contain"
        style={{
          width: 50,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />
      {/* Avatar container - centered */}
      <div className="relative z-10 flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}

interface NavItem {
  title: string;
  icon: LucideIcon;
  href: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const sidebarSections: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { title: 'Dashboard', icon: PieChart, href: '/dashboard' },
      { title: 'Users', icon: Users, href: '/user-lookup' },
      { title: 'Product Insights', icon: Lightbulb, href: '/product-insights' },
      { title: 'Credit Analytics', icon: CreditCard, href: '/credit-analytics' },
    ],
  },
  {
    label: 'Sessions',
    items: [
      { title: 'Session History', icon: Cloud, href: '/session-history' },
      { title: 'Report Wizards', icon: FileText, href: '/report-wizards' },
    ],
  },
  {
    label: 'TESTING (QA)',
    items: [
      { title: 'Chat & Judge', icon: MessageSquare, href: '/chat-judge' },
      { title: 'Benchmarks', icon: Layers, href: '/benchmarks' },
      { title: 'Metrics History', icon: TrendingUp, href: '/metrics-history' },
      { title: 'Performance', icon: Clock, href: '/performance' },
    ],
  },
  {
    label: 'Analysis',
    items: [
      { title: 'Comparison', icon: GitCompare, href: '/comparison' },
      { title: 'Search Outputs', icon: Search, href: '/search-agent-outputs' },
    ],
  },
  {
    label: 'Tools',
    items: [
      { title: 'System Info', icon: Info, href: '/info' },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
  onAgentToggle?: () => void;
}

export function Sidebar({ isOpen, onClose, onAgentToggle }: SidebarProps) {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <motion.aside
        initial={{ x: -224 }}
        animate={{ x: isOpen ? 0 : -224 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        className={cn(
          'fixed lg:sticky top-0 left-0 h-screen w-56 bg-sidebar border-r border-sidebar-border z-50',
          'flex flex-col',
          'lg:translate-x-0 lg:!transform-none'
        )}
      >
        {/* Logo */}
        <div className="h-14 flex items-center px-5 border-b border-sidebar-border">
          <Link
            to="/dashboard"
            className="flex items-center gap-2.5"
            onClick={onClose}
          >
            <img
              src="/gieni_qa.png"
              alt="Gieni Analytics"
              className="h-7 w-7"
            />
            <span className="text-base font-heading text-sidebar-foreground">
              Gieni Analytics
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {sidebarSections.map((section, sectionIdx) => (
            <div key={section.label} className={cn(sectionIdx > 0 && 'mt-6')}>
              {/* Section Label */}
              <div className="px-3 mb-2">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  {section.label}
                </span>
              </div>

              {/* Section Items */}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href || 
                    (item.href !== '/dashboard' && location.pathname.startsWith(item.href));

                  return (
                    <motion.div
                      key={item.href}
                      whileHover={{ x: 4 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    >
                      <Link
                        to={item.href}
                        onClick={onClose}
                        className={cn(
                          'group flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px]',
                          'transition-all duration-150',
                          isActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-muted/50'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-4 w-4 shrink-0 transition-colors',
                            isActive
                              ? 'text-primary'
                              : 'text-muted-foreground group-hover:text-sidebar-foreground'
                          )}
                        />
                        <span className="truncate">{item.title}</span>
                        {isActive && (
                          <div className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
                        )}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Data Agent Button */}
        {onAgentToggle && (
          <div className="border-t border-sidebar-border px-3 py-2">
            <motion.div
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <button
                onClick={() => { onAgentToggle(); onClose?.(); }}
                className={cn(
                  'group flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] w-full',
                  'transition-all duration-150',
                  'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-muted/50'
                )}
              >
                <Bot className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-sidebar-foreground transition-colors" />
                <span className="truncate">Data Agent</span>
              </button>
            </motion.div>
          </div>
        )}

        {/* User Profile */}
        {!authLoading && user && (
          <div className="border-t border-sidebar-border px-3 py-3">
            <div className="flex items-center gap-3 px-2">
              {isAdminUser(user.email) ? (
                <FlameRing>
                  <Avatar className="h-8 w-8">
                    {user.imageUrl && <AvatarImage src={user.imageUrl} alt={getUserDisplayName(user)} />}
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                      {getUserInitials(user)}
                    </AvatarFallback>
                  </Avatar>
                </FlameRing>
              ) : (
                <Avatar className="h-8 w-8">
                  {user.imageUrl && <AvatarImage src={user.imageUrl} alt={getUserDisplayName(user)} />}
                  <AvatarFallback className="text-xs bg-primary/10 text-primary">
                    {getUserInitials(user)}
                  </AvatarFallback>
                </Avatar>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {getUserDisplayName(user)}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user.email}
                </p>
              </div>
            </div>
          </div>
        )}
      </motion.aside>
    </>
  );
}
